import { useCallback, useRef, useState } from 'react'
import type { CanvasBlock } from '@/lib/cms/card-registry'
import { getCardDef } from '@/lib/cms/card-registry'
import type { HistorySnapshot } from '@/pages/admin/cms-editor/types'

/* ============================================================
   useCanvasHistory — Undo/Redo for canvas block array
   Mirrors useEditHistory but typed for CanvasBlock[].
   Also generates HistorySnapshot entries for the History panel.
   ============================================================ */

interface HistoryState {
  past: CanvasBlock[][]
  present: CanvasBlock[]
  future: CanvasBlock[][]
}

const MAX_HISTORY = 30

/** Auto-generate a human-readable label describing what changed */
function describeChange(prev: CanvasBlock[], next: CanvasBlock[]): string {
  if (prev.length === 0 && next.length > 0) return 'Initial layout'
  if (next.length > prev.length) {
    const added = next.find(b => !prev.some(p => p.id === b.id))
    if (added) {
      const def = getCardDef(added.type)
      return `Added ${def?.label ?? added.type}`
    }
    return `Added ${next.length - prev.length} block(s)`
  }
  if (next.length < prev.length) {
    const removed = prev.find(b => !next.some(n => n.id === b.id))
    if (removed) {
      const def = getCardDef(removed.type)
      return `Removed ${def?.label ?? removed.type}`
    }
    return `Removed ${prev.length - next.length} block(s)`
  }
  // Same count — check for content or style changes
  const changed = next.find((b, i) => {
    const old = prev[i]
    if (!old || old.id !== b.id) return true
    if (JSON.stringify(old.data) !== JSON.stringify(b.data)) return true
    if (JSON.stringify(old.styles) !== JSON.stringify(b.styles)) return true
    if (old.colSpan !== b.colSpan) return true
    return false
  })
  if (changed) {
    // Check for reorder (same IDs but different order)
    const prevIds = prev.map(b => b.id).join(',')
    const nextIds = next.map(b => b.id).join(',')
    if (prevIds !== nextIds) return 'Reordered blocks'
    const def = getCardDef(changed.type)
    return `Edited ${def?.label ?? changed.type}`
  }
  return 'Updated layout'
}

export function useCanvasHistory(initialBlocks: CanvasBlock[] = []) {
  const [history, setHistory] = useState<HistoryState>({
    past: [],
    present: initialBlocks,
    future: [],
  })

  const [snapshots, setSnapshots] = useState<HistorySnapshot[]>([])
  const [snapshotIndex, setSnapshotIndex] = useState(-1)
  const initialized = useRef(false)

  /** Reset with fresh data (e.g. loaded from DB) */
  const reset = useCallback((blocks: CanvasBlock[]) => {
    initialized.current = true
    setHistory({ past: [], present: blocks, future: [] })
    setSnapshots([])
    setSnapshotIndex(-1)
  }, [])

  /** Set blocks directly without adding to history (for initial load) */
  const setBlocks = useCallback((blocks: CanvasBlock[]) => {
    setHistory((h) => ({ ...h, present: blocks }))
  }, [])

  /** Push a new state (user made an edit) */
  const push = useCallback((updater: CanvasBlock[] | ((prev: CanvasBlock[]) => CanvasBlock[])) => {
    setHistory((h) => {
      const next = typeof updater === 'function' ? updater(h.present) : updater
      const label = describeChange(h.present, next)

      // Create snapshot for this edit
      const snap: HistorySnapshot = {
        id: `snap-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name: label,
        timestamp: Date.now(),
        blocks: next,
      }

      setSnapshots(prev => {
        // If we're not at the end (user undid then made new edit), trim future snapshots
        const trimmed = snapshotIndex >= 0 ? prev.slice(0, snapshotIndex + 1) : prev
        const updated = [...trimmed, snap].slice(-MAX_HISTORY)
        setSnapshotIndex(updated.length - 1)
        return updated
      })

      return {
        past: [...h.past.slice(-(MAX_HISTORY - 1)), h.present],
        present: next,
        future: [], // Clear redo stack on new edit
      }
    })
  }, [snapshotIndex])

  /** Undo last change */
  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.past.length === 0) return h
      const previous = h.past[h.past.length - 1]
      setSnapshotIndex(i => Math.max(-1, i - 1))
      return {
        past: h.past.slice(0, -1),
        present: previous,
        future: [h.present, ...h.future],
      }
    })
  }, [])

  /** Redo last undone change */
  const redo = useCallback(() => {
    setHistory((h) => {
      if (h.future.length === 0) return h
      const next = h.future[0]
      setSnapshotIndex(i => i + 1)
      return {
        past: [...h.past, h.present],
        present: next,
        future: h.future.slice(1),
      }
    })
  }, [])

  /** Restore to a specific snapshot index */
  const restoreToSnapshot = useCallback((index: number) => {
    const snap = snapshots[index]
    if (!snap) return
    setHistory(h => ({
      past: [...h.past, h.present],
      present: snap.blocks,
      future: [],
    }))
    setSnapshotIndex(index)
  }, [snapshots])

  /** Rename a snapshot */
  const nameSnapshot = useCallback((id: string, name: string) => {
    setSnapshots(prev => prev.map(s => s.id === id ? { ...s, name } : s))
  }, [])

  return {
    blocks: history.present,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    push,
    undo,
    redo,
    reset,
    setBlocks,
    // History panel
    snapshots,
    snapshotIndex,
    restoreToSnapshot,
    nameSnapshot,
  }
}
